// ═══════════════════════════════════════════════════════════════════
//  modules/dashboard/DashboardHome.js
//  DashboardHome
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { BouxImg } from "../../composants/BouxImg";
import { KpiCard } from "../../composants/KpiCard";
import { LineChart } from "../../composants/LineChart";
import { Panel } from "../../composants/Panel";
import { StatCle } from "../../composants/StatCle";
import { API_BASE } from "../../socle/api";
import { fmt, timeAgo } from "../../socle/format";
import { useIsMobile } from "../../socle/hooks";
import { THEME } from "../../socle/theme";
import { WikiPanel } from "../wiki/WikiPanel";
import { JOURS_LMS, SRV } from "./constantes";
import { heatScale } from "./utils";
import { VersionsModal } from "./VersionsModal";

export function DashboardHome({ token, user, onNavigate }) {
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [now, setNow] = useState(new Date());
  const [showVersions, setShowVersions] = useState(false);

  const load = useCallback(async () => {
    try { const res = await fetch(`${API_BASE}/api/dashboard/summary`, { headers: { Authorization: `Bearer ${token}` } }); if (!res.ok) throw new Error(); setData(await res.json()); setErr(false); }
    catch { setErr(true); }
  }, [token]);
  useEffect(() => { load(); const id = setInterval(load, 15000); const clk = setInterval(() => setNow(new Date()), 1000); return () => { clearInterval(id); clearInterval(clk); }; }, [load]);

  const prenom = user.discordUsername || (user.email || "").split("@")[0];
  const heure = now.getHours();
  const salut = heure < 6 ? "Bonne nuit" : heure < 12 ? "Bonjour" : heure < 18 ? "Bon après-midi" : "Bonsoir";
  // msgJeu et msgDis existaient ici sans être utilisés : les deux compteurs
  // correspondants du Dashboard sont encore en attente des plugins.
  const conn = data?.spark?.connectes || [];
  const dlt = (a) => (a.length >= 2 ? a[a.length - 1] - a[a.length - 2] : null);

  const hm = Array.from({ length: 24 }, () => Array(7).fill(null));
  let hmMax = 0;
  (data?.heatmap || []).forEach((r) => { const day = (r.j + 5) % 7; hm[r.h][day] = r.moy; if (r.moy > hmMax) hmMax = r.moy; });
  const j30 = data?.joueurs30j || [], t30 = data?.tickets30j || [], sc = data?.statsCles || {};
  const ver = data?.version;
  const verLabel = ver ? `${ver.version}${ver.phase ? " · " + ver.phase : ""}` : "Version : en construction";

  return (
    <div>
      <style>{`@keyframes dashPulse{0%,100%{opacity:1}50%{opacity:.35}} @media print{.no-print{display:none!important}}`}</style>

      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 18 }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ color: THEME.text, fontSize: 22, fontWeight: 800 }}>{salut} {prenom} 👋</div>
          <div style={{ color: THEME.text, fontSize: 20, fontWeight: 800, marginTop: 2 }}>Bienvenue sur le centre de contrôle Boxoria.</div>
          <div style={{ color: THEME.textMuted, fontSize: 13, marginTop: 6 }}>
            {data?.devsActifs > 0 ? <>Aujourd'hui, <b style={{ color: THEME.purple }}>{data.devsActifs} membre{data.devsActifs > 1 ? "s" : ""}</b> de l'équipe {data.devsActifs > 1 ? "ont" : "a"} déjà contribué.</> : "Bonne journée de travail sur le projet."}
          </div>
        </div>
        <div className="no-print" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button onClick={() => setShowVersions(true)} title="Voir les versions & patchnotes" style={{ background: THEME.purpleDim, border: `1px solid ${THEME.purple}`, borderRadius: 8, padding: "8px 12px", color: THEME.purple, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>🏷️ {verLabel}</button>
          <div style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 8, padding: "8px 12px", color: THEME.textMuted, fontSize: 12, textTransform: "capitalize" }}>📅 {now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
          <div style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 8, padding: "8px 12px", color: THEME.gold, fontSize: 12, fontWeight: 700 }}>🕒 {now.toLocaleTimeString("fr-FR")}</div>
          <button onClick={() => window.print()} style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 8, padding: "8px 12px", color: THEME.text, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>🖨️ Export PDF</button>
        </div>
      </div>

      {err && <div style={{ background: THEME.redDim, color: THEME.red, border: `1px solid ${THEME.red}`, borderRadius: 8, padding: 10, marginBottom: 16, fontSize: 13 }}>Impossible de joindre le serveur — nouvelle tentative…</div>}

      {/* KPI */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
        <KpiCard icon="👥" label="Joueurs connectés" value={fmt(data?.kpis?.joueursConnectes)} color={THEME.purple} spark={conn} delta={dlt(conn)} deltaUp={dlt(conn) >= 0} />
        <KpiCard icon="🌐" label="Joueurs uniques" value={fmt(data?.kpis?.joueursUniques)} color={THEME.accent} sub="base totale" />
        <KpiCard icon="🎫" label="Tickets ouverts" value={fmt(data?.kpis?.ticketsOuverts)} color={THEME.amber} sub="à traiter" />
        <KpiCard icon="✅" label="Tâches en cours" value={fmt(data?.kpis?.tachesEnCours)} color={THEME.emerald} sub="en cours" />
        {/* Le back calculait deja ces deux valeurs ; le front affichait un
            chantier sans les lire. Tant que rien ne poste sur
            /api/dashboard/event, on ecrit « jamais alimente » — pas 0, qui se
            lirait « personne n'a parle aujourd'hui ». */}
        <KpiCard icon="💬" label="Messages jeu (jour)"
          value={data?.statsCles?.messagerieBranchee ? fmt(data?.kpis?.messagesJeuJour) : "—"}
          color={THEME.purple}
          sub={data?.statsCles?.messagerieBranchee ? "aujourd'hui" : "jamais alimenté"} />
        <KpiCard icon="🗨️" label="Messages Discord (jour)"
          value={data?.statsCles?.messagerieBranchee ? fmt(data?.kpis?.messagesDiscordJour) : "—"}
          color={THEME.gold}
          sub={data?.statsCles?.messagerieBranchee ? "aujourd'hui" : "jamais alimenté"} />
      </div>

      {/* On y travaille */}
      <Panel title="🔧 On y travaille" style={{ marginBottom: 16 }}>
        {(!data || data.onYTravaille.length === 0) && <div style={{ color: THEME.textDim, fontSize: 13 }}>Aucune tâche mise en avant. Utilise le bouton « Mettre en avant » dans la Gestion de Projet.</div>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
          {data?.onYTravaille.map((h) => (
            <div key={h.task_id} style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 10, padding: 12 }}>
              <div style={{ color: THEME.gold, fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{h.title}</div>
              <div style={{ color: THEME.textMuted, fontSize: 12, whiteSpace: "pre-wrap", lineHeight: 1.45 }}>{h.resume}</div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Wiki Staff */}
      <WikiPanel token={token} onNavigate={onNavigate} />

      {/* SERVEURS · ACTIVITÉ · ACCUEIL */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16, marginBottom: 16 }}>
        <Panel title="État des serveurs">
          {!data && <div style={{ color: THEME.textDim, fontSize: 13 }}>Lecture…</div>}
          {data && Object.entries(data.live.servers).map(([name, v]) => { const s = SRV[v.statut] || SRV.hors_ligne; return (
            <div key={name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: `1px solid ${THEME.border}` }}>
              <span style={{ fontSize: 16 }}>🧊</span><span style={{ color: THEME.text, fontSize: 13, fontWeight: 600, textTransform: "capitalize" }}>{name}</span>
              <span style={{ color: THEME.textMuted, fontSize: 12, marginLeft: 8 }}>{v.statut === "en_ligne" ? `${v.joueurs} joueurs` : ""}</span>
              <span style={{ marginLeft: "auto", background: s.b, color: s.c, fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 6 }}>{s.t}</span>
              <span style={{ color: v.tps == null ? THEME.textDim : v.tps >= 19 ? THEME.emerald : v.tps >= 15 ? THEME.amber : THEME.red, fontSize: 12, fontWeight: 600, width: 64, textAlign: "right" }}>{v.tps != null ? `${v.tps} TPS` : "—"}</span>
            </div>); })}
          {data && [["Redis", data.live.infra.db], ["Base SQL", data.live.infra.db], ["API", data.live.infra.api], ["Site Web", data.live.infra.site]].map(([nm, st], i) => { const s = SRV[st] || SRV.hors_ligne; return (
            <div key={nm + i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: `1px solid ${THEME.border}` }}>
              <span style={{ fontSize: 16 }}>{nm === "Base SQL" ? "🗄️" : nm === "API" ? "🔌" : nm === "Redis" ? "🧠" : "🌐"}</span><span style={{ color: THEME.text, fontSize: 13, fontWeight: 600 }}>{nm}</span>
              <span style={{ marginLeft: "auto", background: s.b, color: s.c, fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 6 }}>{s.t}</span>
            </div>); })}
        </Panel>

        <Panel title="Activité récente">
          {(!data || data.activity.length === 0) && <div style={{ color: THEME.textDim, fontSize: 13 }}>Aucune action.</div>}
          {data?.activity.map((a, i, arr) => (
            <div key={i} style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: i < arr.length - 1 ? `1px solid ${THEME.border}` : "none" }}>
              <span style={{ color: THEME.textDim, fontSize: 11, width: 42, flexShrink: 0 }}>{new Date(a.ts).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
              <span style={{ fontSize: 15 }}>{a.icon}</span>
              <div style={{ minWidth: 0 }}><div style={{ color: THEME.text, fontSize: 13, fontWeight: 600 }}>{a.titre}</div><div style={{ color: THEME.textDim, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.detail || timeAgo(a.ts)}</div></div>
            </div>
          ))}
        </Panel>

        <div style={{ background: `linear-gradient(150deg, ${THEME.purpleDim}, ${THEME.panel} 70%)`, border: `1px solid ${THEME.border}`, borderRadius: 14, padding: 22, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
          <BouxImg height={92} />
          <div style={{ color: THEME.gold, fontSize: 18, fontWeight: 800, marginTop: 12 }}>Bienvenue sur Boxoria</div>
          <div style={{ color: THEME.textMuted, fontSize: 13, marginTop: 6 }}>Un monde. Une communauté.<br />Des aventures infinies.</div>
          <button onClick={() => setShowVersions(true)} className="no-print" style={{ marginTop: 16, background: THEME.purple, border: "none", color: "#0D1117", fontWeight: 700, borderRadius: 8, padding: "9px 16px", cursor: "pointer", fontSize: 13 }}>Voir les patchnotes →</button>
        </div>
      </div>

      {/* HEATMAP · JOUEURS · TICKETS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16, marginBottom: 16 }}>
        <Panel title="Fréquentation hebdomadaire par heure">
          {hmMax === 0 ? <div style={{ color: THEME.textDim, fontSize: 13 }}>Pas encore de données — relevé toutes les 5 min.</div> : (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "34px repeat(7, 1fr)", gap: 3 }}>
                <div />{JOURS_LMS.map((d) => <div key={d} style={{ color: THEME.textDim, fontSize: 9, textAlign: "center" }}>{d}</div>)}
                {hm.flatMap((row, h) => [(h % 4 === 0) ? <div key={`l${h}`} style={{ color: THEME.textDim, fontSize: 9, display: "flex", alignItems: "center" }}>{String(h).padStart(2, "0")}h</div> : <div key={`l${h}`} />, ...row.map((v, d) => <div key={`${h}-${d}`} title={v != null ? `${JOURS_LMS[d]} ${h}h — ${v} joueurs` : ""} style={{ height: 9, borderRadius: 2, background: v == null ? THEME.surface : heatScale(v / hmMax) }} />)])}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}><span style={{ color: THEME.textDim, fontSize: 10 }}>0</span><div style={{ flex: 1, height: 8, borderRadius: 4, background: "linear-gradient(90deg, rgb(26,11,46), rgb(181,23,158), rgb(247,37,133), rgb(255,133,0), rgb(255,214,10))" }} /><span style={{ color: THEME.textDim, fontSize: 10 }}>Max</span></div>
            </div>
          )}
        </Panel>
        <Panel title="Nombre de joueurs (30 jours)">
          {j30.every((d) => d.valeur === 0) ? <div style={{ color: THEME.textDim, fontSize: 13 }}>La courbe se construit (relevé 5 min).</div> : <LineChart area labels={j30.map((d) => d.jour)} series={[{ color: THEME.purple, values: j30.map((d) => d.valeur) }]} />}
        </Panel>
        <Panel title="Tickets (30 jours)">
          <div style={{ display: "flex", gap: 14, marginBottom: 6, fontSize: 11 }}><span style={{ color: THEME.red }}>● Ouverts</span><span style={{ color: THEME.emerald }}>● Résolus</span></div>
          <LineChart labels={t30.map((d) => d.jour)} series={[{ color: THEME.red, values: t30.map((d) => d.ouverts) }, { color: THEME.emerald, values: t30.map((d) => d.resolus) }]} />
        </Panel>
      </div>

      {/* STATS CLÉS · DISCORD PAR SALON · ALERTES */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr 1fr", gap: 16 }}>
        <Panel title="Statistiques clés">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            <StatCle icon="⭐" label="Satisfaction tickets" value={sc.satisfaction != null ? `${sc.satisfaction}/5` : "—"} sub={sc.satisfactionNb ? `${sc.satisfactionNb} avis` : "aucun avis"} />
            <StatCle icon="👥" label="Joueurs uniques" value={fmt(sc.joueursUniques != null ? sc.joueursUniques : data?.kpis?.joueursUniques)} sub="base totale" />
            <StatCle icon="💬" label="Messages jeu"
              value={sc.messagesJeuTotal != null ? fmt(sc.messagesJeuTotal) : "—"}
              sub={sc.messagesJeuTotal != null ? "depuis le début" : "jamais alimenté"} />
            <StatCle icon="🗨️" label="Messages Discord"
              value={sc.messagesDiscordTotal != null ? fmt(sc.messagesDiscordTotal) : "—"}
              sub={sc.messagesDiscordTotal != null ? "depuis le début" : "jamais alimenté"} />
            <StatCle icon="🏝️" label="Îles créées" value={sc.iles != null ? fmt(sc.iles) : "—"} sub="bbox" />
            <StatCle icon="🦉" label="Boux adoptés"
              value={sc.boux != null ? fmt(sc.boux) : "—"}
              sub={sc.boux != null
                ? (sc.bouxJoueurs === 1 ? "1 joueur" : `${fmt(sc.bouxJoueurs)} joueurs`)
                : "table absente"} />
            <StatCle icon="⏳" label="Temps de jeu" value="🚧" sub="en construction" />
            <StatCle icon="🗺️" label="Quêtes réalisées" value="🚧" sub="en construction" />
          </div>
        </Panel>

        <Panel title="Discord par salon (7 j)">
          {/* Le back renvoyait deja `discordParSalon` ; ce panneau ne le lisait
              pas. Il restera vide tant que le bot ne poste pas, mais il dit
              POURQUOI au lieu d'annoncer un chantier qui n'existe plus. */}
          {(sc.discordParSalon || []).length === 0 ? (
            <div style={{ color: THEME.textDim, fontSize: 13, lineHeight: 1.5 }}>
              Aucun message compté sur 7 jours.
              {!sc.messagerieBranchee && (
                <div style={{ marginTop: 6, color: THEME.textMuted, fontSize: 12 }}>
                  Le bot Discord n'appelle pas encore <code>/api/dashboard/event</code> — la route
                  l'attend, elle est prête.
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {sc.discordParSalon.map((c) => {
                const maxi = Math.max(...sc.discordParSalon.map((x) => x.n), 1);
                return (
                  <div key={c.salon}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: THEME.text }}>
                      <span>#{c.salon}</span>
                      <b style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(c.n)}</b>
                    </div>
                    <div style={{ height: 4, background: THEME.surface, borderRadius: 2, marginTop: 3 }}>
                      <div style={{ height: 4, width: `${Math.round((c.n / maxi) * 100)}%`, background: THEME.gold, borderRadius: 2 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>

        <Panel title="Alertes techniques">
          {(!data || data.alertes.length === 0) && <div style={{ color: THEME.emerald, fontSize: 13 }}>✓ Tout est sous contrôle.</div>}
          {data?.alertes.map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 10px", marginBottom: 6, borderRadius: 8, background: a.niveau === "rouge" ? THEME.redDim : THEME.amberDim, border: `1px solid ${a.niveau === "rouge" ? THEME.red : THEME.amber}` }}>
              <span>{a.niveau === "rouge" ? "🔴" : "⚠️"}</span><span style={{ color: a.niveau === "rouge" ? THEME.red : THEME.amber, fontSize: 12 }}>{a.texte}</span>
            </div>
          ))}
        </Panel>
      </div>

      {showVersions && <VersionsModal token={token} user={user} onClose={() => setShowVersions(false)} />}
    </div>
  );
}
